import symbolGold from '@powerful/brand/assets/logo/powerful-connect-symbol-gold.svg';
import { lazy, Suspense, useLayoutEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Route, Routes } from 'react-router-dom';

import { SetupIncompleteBanner } from '../components/business/setup-incomplete-banner';
import { LocationControl } from '../components/discovery/location-control';
import { ShellChrome } from '../components/layout/shell-chrome';
import { BottomTabs } from '../components/nav/bottom-tabs';
import { CONSUMER_NAV_ICONS, ConsumerSidebar } from '../components/nav/consumer-sidebar';
import { ContextSwitcher } from '../components/nav/context-switcher';
import { CONSUMER_NAV } from '../components/nav/nav-items';
import { rotaRelativa } from '../components/nav/rota-relativa';
import { DiscoveryLocationProvider } from '../lib/geo/discovery-location';
import { HomePage } from '../pages/discovery/home';
import { SearchPage } from '../pages/discovery/search';
import { Placeholder } from '../pages/placeholder';
import { useShellContext } from '../contexts/shell-context';

const AccountSettingsPage = lazy(() => import('../pages/settings/account-settings').then((module) => ({ default: module.AccountSettingsPage })));
const DeleteAccountPage = lazy(() => import('../pages/settings/delete-account').then((module) => ({ default: module.DeleteAccountPage })));

/** Base da conta no shell consumer, derivada do separador Perfil (fonte única: `nav-items.ts`). */
const ACCOUNT_PATH = CONSUMER_NAV.find((item) => item.id === 'profile')?.to ?? '/home/perfil';

/** Preferência visual por dispositivo — não é dado da conta, e falhar a leitura só volta ao padrão. */
export const SIDEBAR_STORAGE_KEY = 'powerful.consumer.sidebar';

function readExpanded(): boolean {
  try {
    return localStorage.getItem(SIDEBAR_STORAGE_KEY) === 'expanded';
  } catch {
    return false;
  }
}

/**
 * Shell Consumidor (B2C) — PRD §3.5.1 com o refino **R12** do canvas T2.1.
 *
 * *"Home · Mapa · Agendamentos · Favoritos · Perfil. Rotas `/home/*`."*
 *
 * Abaixo de 1024 px: bottom tabs (thumb zone, PRD §6.2). A partir de 1024 px a barra dá lugar a
 * uma sidebar colapsada (72 px) — no desktop a descoberta precisa da largura para o painel de
 * filtros e o feed, e uma barra inferior num monitor é navegação fora do lugar. A troca é por CSS;
 * os dois `<nav>` coexistem no DOM com rótulos distintos (`landmark-unique`).
 */
export function ConsumerShell() {
  const { t } = useTranslation();
  const { contexto, alternar } = useShellContext();
  const [expanded, setExpanded] = useState(readExpanded);

  // `/home/*` é também uma escolha explícita de contexto (deep link ou botão Voltar). Sincronizar
  // antes do paint impede o shell consumer de exibir um switcher ainda invertido pelo histórico.
  useLayoutEffect(() => {
    if (contexto === 'professional') alternar();
  }, [alternar, contexto]);

  const toggleSidebar = () => {
    setExpanded((atual) => {
      const next = !atual;
      try {
        localStorage.setItem(SIDEBAR_STORAGE_KEY, next ? 'expanded' : 'collapsed');
      } catch {
        /* preferência opcional */
      }
      return next;
    });
  };

  return (
    <DiscoveryLocationProvider>
      <div className={expanded ? 'pc-cshell is-expanded' : 'pc-cshell'}>
        <ConsumerSidebar itens={CONSUMER_NAV} label={t('nav.primaryDesktop')} expanded={expanded} onToggle={toggleSidebar} />
        <div className="pc-ccol">
          <ShellChrome
            header={
              <header className="pc-header">
                <div className="pc-header-in">
                  <Link className="pc-brand hit" to="/home" aria-label={t('discovery.brandHome')}>
                    <img src={symbolGold} alt="" width={30} height={30} />
                    <span className="pc-brand-name">Powerful Connect</span>
                  </Link>
                  <div className="pc-header-end">
                    <LocationControl />
                    <ContextSwitcher />
                  </div>
                </div>
              </header>
            }
            nav={
              <BottomTabs
                itens={CONSUMER_NAV}
                label={t('nav.primaryMobile')}
                icones={CONSUMER_NAV_ICONS}
                className="lg:hidden"
              />
            }
          >
            <div data-testid="consumer-shell" className="flex min-w-0 flex-1 flex-col">
              {/*
                Fora do `<Routes>`, e de propósito: o lembrete de negócio por configurar acompanha todos
                os separadores do shell, não uma rota. Informa sem bloquear — toda conta tem base
                consumer (PRD §3.5), e prender o app até concluir um fluxo de 25–45 min seria castigo,
                não lembrete.
              */}
              <SetupIncompleteBanner />

              <Suspense fallback={<p role="status" aria-live="polite">{t('state.loading')}</p>}>
              <Routes>
                {/* T2.10 F3 — resultados, filtros e ordenação; estado inteiro na URL (C2). */}
                <Route path="busca" element={<SearchPage />} />
                {/*
                  As rotas nascem dos MESMOS itens que desenham os tabs: um separador que apontasse para
                  rota inexistente cairia no catch-all, e o utilizador veria "página não encontrada" ao
                  tocar num item da própria navegação.
                */}
                {CONSUMER_NAV.map((item) => (
                  <Route
                    key={item.id}
                    /*
                      Path **relativo ao prefixo do shell**, não absoluto: o `app-router` monta este
                      shell em `/home/*`, e um `<Routes>` aninhado só vê o que sobra do splat.
                      Derivar de `item.to` mantém `nav-items.ts` como fonte única.
                    */
                    path={rotaRelativa(item.to, '/home')}
                    element={
                      item.id === 'home'
                        ? <HomePage />
                        : item.id === 'profile'
                          ? <AccountSettingsPage basePath={item.to} testId={`page-${item.id}`} />
                          : <Placeholder titulo={t(item.labelKey)} testId={`page-${item.id}`} />
                    }
                  />
                ))}
                {/* T1.17 — exclusão de conta encontrável a partir do Perfil (App Store 5.1.1(v)). */}
                <Route path="perfil/eliminar-conta" element={<DeleteAccountPage backTo={ACCOUNT_PATH} />} />
                <Route path="*" element={<Placeholder titulo={t('state.empty')} testId="page-consumer-fallback" />} />
              </Routes>
              </Suspense>
            </div>
          </ShellChrome>
        </div>
      </div>
    </DiscoveryLocationProvider>
  );
}
